import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { ChevronIcon } from 'assets/icons';
import { vaultDetailActions } from 'context/vault-detail-context/action-types';
import { VaultDetailContext } from 'context/vault-detail-context';
import { LocalStorageApi } from 'helpers/local-storage';
import { classnames } from 'helpers/utils';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { RecordMode, RespondMenu, RespondMode } from 'common/respond-menu';
import { PromptFileChooser } from 'common/upload-modal';
import { VideoCallWaitingRoom } from 'pages/vault-detail/components/video-call-waiting-room';
import { RecordNow } from 'pages/vault-detail/components/tabs/planner/components/record-now';
import { PromptCard } from './prompt-card';
import styles from './prompts-section.module.scss';

export type DashboardPrompt = {
  content: AlbumContentSerialized;
  albumId: number;
  // Where an answer to it is saved (see albumDisplayName).
  albumName: string;
  // Not in an album of the user's own yet: no album badge on its card.
  inDefaultAlbum?: boolean;
};

interface PromptsSectionProps {
  vaultId: number;
  // For the video call's waiting room.
  vault?: VaultType;
  prompts: DashboardPrompt[];
  // Who asked, for prompts that don't say (the vault's owner).
  defaultAskedBy: string;
  // The album's memories, for the answering window's "Answered" list.
  answeredIn?: (albumId: number) => AlbumContentSerialized[];
}

const CARD_WIDTH = 328;
const CARD_GAP = 24;
const HIDDEN_KEY = 'dashboard_prompts_hidden';

const isRecordMode = (mode: RespondMode): mode is RecordMode =>
  mode === 'journal' || mode === 'audio' || mode === 'video';

// The vault's unanswered interview questions, above the albums (Figma: GS UI
// / Design System, node 32474:23113). "Respond" asks how (the "New Memory"
// options), then answers in the prompt's album, the way the album planner
// does.
export const PromptsSection = ({
  vaultId,
  vault,
  prompts,
  defaultAskedBy,
  answeredIn,
}: PromptsSectionProps) => {
  const { dispatch } = useContext(VaultDetailContext);
  const [hidden, setHidden] = useState<boolean>(
    () => !!LocalStorageApi.get(HIDDEN_KEY),
  );
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(1);
  // The card whose "Respond with" menu is open, and where it sits.
  const [menu, setMenu] = useState<{
    prompt: DashboardPrompt;
    anchor: DOMRect;
  } | null>(null);
  const [responding, setResponding] = useState<{
    prompt: DashboardPrompt;
    mode: RespondMode;
  } | null>(null);
  const recording =
    responding && isRecordMode(responding.mode)
      ? { prompt: responding.prompt, mode: responding.mode }
      : null;
  const rowRef = useRef<HTMLDivElement>(null);

  // As many whole cards as fit across the dashboard.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    const measure = () =>
      setPerPage(
        Math.max(
          1,
          Math.floor((row.clientWidth + CARD_GAP) / (CARD_WIDTH + CARD_GAP)),
        ),
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [hidden]);

  const total = prompts.length;
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * perPage;
  const visible = prompts.slice(start, start + perPage);

  const toggleHidden = () => {
    setHidden((prev) => {
      LocalStorageApi.set(HIDDEN_KEY, !prev);
      return !prev;
    });
  };

  // The album's other prompts stay available in the recorder; the one
  // clicked comes first, so it's the one selected.
  const albumTopics = (prompt: DashboardPrompt) => [
    prompt.content,
    ...prompts
      .filter(
        (other) =>
          other.albumId === prompt.albumId &&
          other.content.id !== prompt.content.id,
      )
      .map((other) => other.content),
  ];

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <span className={styles.title}>Prompts</span>
        <button
          type="button"
          className={styles.hideButton}
          onClick={toggleHidden}
        >
          {hidden ? 'Show' : 'Hide'}
          <ChevronIcon
            className={classnames(styles.hideChevron, {
              [styles.hideChevronUp]: !hidden,
            })}
          />
        </button>
        {!hidden && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 0}
              aria-label="Previous prompts"
            >
              <ChevronIcon className={styles.pageLeftIcon} />
            </button>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage >= pageCount - 1}
              aria-label="More prompts"
            >
              <ChevronIcon className={styles.pageRightIcon} />
            </button>
          </div>
        )}
      </div>
      {!hidden && (
        <div className={styles.cards} ref={rowRef}>
          {visible.map((prompt) => (
            <PromptCard
              key={prompt.content.id}
              question={prompt.content.name || 'Untitled prompt'}
              askedBy={prompt.content.user?.name || defaultAskedBy}
              albumName={prompt.inDefaultAlbum ? undefined : prompt.albumName}
              onRespond={(anchor) => setMenu({ prompt, anchor })}
            />
          ))}
        </div>
      )}
      {menu && (
        <RespondMenu
          anchor={menu.anchor}
          onClose={() => setMenu(null)}
          onPick={(mode) => setResponding({ prompt: menu.prompt, mode })}
        />
      )}
      {recording && (
        <RecordNow
          vaultId={vaultId}
          albumId={recording.prompt.albumId}
          albumName={recording.prompt.albumName}
          topics={albumTopics(recording.prompt)}
          mode={recording.mode}
          initialTopicId={recording.prompt.content.id}
          answered={answeredIn?.(recording.prompt.albumId)}
          onTopicsChanged={() =>
            dispatch({ type: vaultDetailActions.REQUEST_ALBUMS_REFRESH })
          }
          onClose={() => setResponding(null)}
        />
      )}
      {responding?.mode === 'upload' && (
        <PromptFileChooser
          vaultId={vaultId}
          albumId={
            responding.prompt.inDefaultAlbum
              ? undefined
              : responding.prompt.albumId
          }
          promptId={responding.prompt.content.id}
          onClose={() => setResponding(null)}
        />
      )}
      {responding?.mode === 'videocall' && vault && (
        <VideoCallWaitingRoom
          vault={vault}
          albumId={responding.prompt.albumId}
          onClose={() => setResponding(null)}
        />
      )}
    </section>
  );
};
